import React, { useRef, useEffect, Suspense, useState, useMemo, useCallback } from 'react';
import { OrthographicCamera } from '@react-three/drei';
import { useFrame, useThree, extend } from '@react-three/fiber';
import { SafeHdrEnvironment } from './SafeHdrEnvironment';
import { isTextureUploadReady } from '../../utils/textureUploadReady';
// Post-processing disabled - causes "Cannot read properties of undefined (reading 'length')"
// crashes with current three.js/postprocessing versions. See TODO in render section.
// import { EffectComposer, Bloom, Vignette } from '@react-three/postprocessing';
import * as THREE from 'three';
import Avatar from '../Avatar/Avatar';
import PortraitFramingResponder from './PortraitFramingResponder';
import { resolveAvatarRuntimeAssetUrl, resolveBackgroundAssetUrl } from '../../modelRegistry';
import { resolveBackgroundUpdate, type BackgroundUpdate } from '../../avatarRuntimeConfig';

import type { OrthographicCamera as OrthographicCameraType } from 'three';

// Extend Three.js elements for R3F JSX
extend({
  AmbientLight: THREE.AmbientLight,
  DirectionalLight: THREE.DirectionalLight
});

// Background config type
interface BackgroundConfig {
  backgroundType: 'image' | 'solid' | 'gradient';
  backgroundImage?: string;
  backgroundColor?: string;
}

// Light configuration type
interface LightConfig {
  enabled: boolean;
  intensity: number;
  color: string;
  position: { x: number; y: number; z: number };
}

// Lighting configuration
interface LightingConfig {
  keyLight?: LightConfig;
  fillLight?: LightConfig;
  rimLight?: LightConfig;
  ambientIntensity?: number;
}

// Default lighting (studio preset)
const DEFAULT_LIGHTING: LightingConfig = {
  keyLight: { enabled: true, intensity: 1.2, color: '#ffffff', position: { x: -2, y: 3, z: 2 } },
  fillLight: { enabled: true, intensity: 0.6, color: '#e8e8ff', position: { x: 2, y: 2, z: 1 } },
  rimLight: { enabled: true, intensity: 0.8, color: '#fffaf0', position: { x: 0, y: 2, z: -2 } },
  ambientIntensity: 0.3
};

// Post-processing config type
interface PostProcessingConfig {
  bloomEnabled: boolean;
  bloomIntensity: number;
  bloomThreshold: number;
  vignetteEnabled: boolean;
  vignetteDarkness: number;
  vignetteOffset: number;
}

// Default post-processing config
const DEFAULT_POSTPROCESSING: PostProcessingConfig = {
  bloomEnabled: false,
  bloomIntensity: 0.4,
  bloomThreshold: 0.9,
  vignetteEnabled: false,
  vignetteDarkness: 0.5,
  vignetteOffset: 0.3
};

// Visual/lighting config type
interface VisualConfig {
  hdrEnvironment?: string;
  envMapIntensity?: number;
  lightingPreset?: string;
  bloomEnabled?: boolean;
  bloomIntensity?: number;
  bloomThreshold?: number;
  vignetteEnabled?: boolean;
  vignetteDarkness?: number;
  vignetteOffset?: number;
}

// HDR environment presets - map preset ID to file path
const HDR_PRESETS: Record<string, string> = {
  'photo_studio': '/images/photo_studio_loft_hall_1k.hdr',
  'soft_light': '/images/soft_light_1k.hdr',
  'dramatic': '/images/dramatic_1k.hdr',
  'neutral': '/images/neutral_1k.hdr',
};
const DEFAULT_BACKGROUND_IMAGE = '/images/officebackground-hd.webp';

type DeviceOrientation = { alpha: number; beta: number; gamma: number };

type SceneProps = {
  avatarRef: React.RefObject<any>;
  userTalking: boolean;
  speak: boolean;
  setSpeak: (value: boolean) => void;
  setRecord: (value: boolean) => void;
  text: string;
  setAudioSource: (source: string | null) => void;
  animateCallSpeak: any;
  setAnimateCallSpeak: (value: any) => void;
  setPlaying: (value: boolean) => void;
  playing: boolean;
  deviceOrientation: DeviceOrientation;
  orientationPermission: string;
  animateHangupButton: () => void;
  resetHangupButton: () => void;
  isMobileCall: boolean;
  isWebrtcCall: boolean;
  isInVoiceCall: boolean;
  isLiveMode?: boolean;
  motionParallaxEnabled?: boolean;
  cameraZoom?: number; // Camera zoom level (default 2000, lower = zoomed out)
  modelUrl?: string; // Model URL (optional)
  modelKey?: string; // Stable key for Avatar identity (defaults to modelUrl).
  skipTeethAnimation?: boolean; // Skip teeth animation for performance
  useDefaultMaterials?: boolean; // Apply Default avatar materials to model
  useEmbeddedTextures?: boolean; // Use embedded textures from GLB (skip external texture loading)
  initialAppearance?: Record<string, string | undefined>;
  /**
   * Background truth known at mount (extracted from the host's
   * initialAppearance/initialConfig). Threaded straight to Bg as a prop
   * so first paint doesn't depend on a window event that can be
   * dispatched before the R3F scene graph (and Bg's listener) exists.
   */
  initialBackground?: Record<string, string> | null;
  avatarInstanceId?: string;
  onAppearanceApplied?: () => void;
};

export default function Scene({
  avatarRef,
  userTalking,
  speak,
  setSpeak,
  setRecord,
  text,
  setAudioSource,
  animateCallSpeak,
  setAnimateCallSpeak,
  setPlaying,
  playing,
  deviceOrientation,
  orientationPermission,
  animateHangupButton,
  resetHangupButton,
  isMobileCall,
  isWebrtcCall,
  isInVoiceCall,
  isLiveMode = false,
  motionParallaxEnabled = false,
  cameraZoom = 2000, // Default zoom level
  modelUrl = '/model.glb', // Default model URL
  modelKey,
  skipTeethAnimation = false, // Skip teeth animation for performance
  useDefaultMaterials = false, // Apply Default avatar materials to model
  useEmbeddedTextures = false, // Use embedded textures from GLB
  initialAppearance,
  initialBackground,
  avatarInstanceId,
  onAppearanceApplied
}: SceneProps) {
  const verboseAvatarDebug = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return new URLSearchParams(window.location.search).get('debugAvatar') === 'true';
    } catch {
      return false;
    }
  }, [avatarInstanceId]);
  const cameraRef = useRef<OrthographicCameraType>(null);
  const { gl } = useThree();
  // Live zoom override from `avatar:config:update` / `avatar:visual:update`
  // events (settings modal preview slider + persisted visual.cameraZoom).
  // Without this the camera zoom was a mount-time prop and every zoom
  // control in the editor silently did nothing.
  const [zoomOverride, setZoomOverride] = useState<number | null>(null);
  const baseZoomRef = useRef(cameraZoom);
  baseZoomRef.current = cameraZoom;
  const [lightingConfig, setLightingConfig] = useState<LightingConfig>(DEFAULT_LIGHTING);
  const [postProcessing, setPostProcessing] = useState<PostProcessingConfig>(DEFAULT_POSTPROCESSING);
  const [visualConfig, setVisualConfig] = useState<VisualConfig>({
    hdrEnvironment: 'photo_studio',
    envMapIntensity: 0.5,
    lightingPreset: 'studio',
    bloomEnabled: false,
    bloomIntensity: 0.4,
    bloomThreshold: 0.9,
    vignetteEnabled: false,
    vignetteDarkness: 0.5,
    vignetteOffset: 0.3
  });

  // Get HDR file path from preset ID
  const hdrFile = resolveAvatarRuntimeAssetUrl(
    HDR_PRESETS[visualConfig.hdrEnvironment || 'photo_studio'] || HDR_PRESETS['photo_studio']
  );

  useEffect(() => {
    try {
      const canvas = gl.domElement;
      if (canvas) {
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.minHeight = '0';
      }
    } catch (error) {
      console.error('Resize error:', error);
    }
  }, [gl]);

  // Listen for visual config updates (HDR, lighting, post-processing)
  useEffect(() => {
    const handleVisualUpdate = (event: CustomEvent) => {
      const payload = event.detail || {};
      if (payload.avatarInstanceId && payload.avatarInstanceId !== avatarInstanceId) {
        return;
      }
      const visual = payload.config?.visual || payload.visual || {};

      // Camera zoom arrives in two shapes:
      //  - absolute orthographic zoom (e.g. 800-2500) from the preview
      //    overlay slider: detail.cameraZoom / detail.config.cameraZoom
      //  - persisted multiplier (0.5-2.5) at visual.cameraZoom, applied
      //    against the mount-time base zoom prop.
      const rawZoom =
        typeof payload.cameraZoom === 'number' ? payload.cameraZoom
        : typeof payload.config?.cameraZoom === 'number' ? payload.config.cameraZoom
        : typeof visual.cameraZoom === 'number' ? visual.cameraZoom
        : undefined;
      if (rawZoom !== undefined && Number.isFinite(rawZoom) && rawZoom > 0) {
        setZoomOverride(rawZoom <= 10 ? rawZoom * baseZoomRef.current : rawZoom);
      }

      // Check for light-related updates
      if (visual.keyLight || visual.fillLight || visual.rimLight || visual.ambientIntensity !== undefined) {
        console.log('💡 Updating lighting config:', visual);
        setLightingConfig(prev => ({
          keyLight: visual.keyLight || prev.keyLight,
          fillLight: visual.fillLight || prev.fillLight,
          rimLight: visual.rimLight || prev.rimLight,
          ambientIntensity: visual.ambientIntensity ?? prev.ambientIntensity
        }));
      }

      // Check for post-processing updates - ensure all values are safe numbers/booleans
      if (visual.bloomEnabled !== undefined || visual.vignetteEnabled !== undefined ||
        visual.bloomIntensity !== undefined || visual.vignetteDarkness !== undefined) {
        console.log('✨ Updating post-processing config:', visual);
        setPostProcessing(prev => ({
          bloomEnabled: typeof visual.bloomEnabled === 'boolean' ? visual.bloomEnabled : prev.bloomEnabled,
          bloomIntensity: typeof visual.bloomIntensity === 'number' ? visual.bloomIntensity : prev.bloomIntensity,
          bloomThreshold: typeof visual.bloomThreshold === 'number' ? visual.bloomThreshold : prev.bloomThreshold,
          vignetteEnabled: typeof visual.vignetteEnabled === 'boolean' ? visual.vignetteEnabled : prev.vignetteEnabled,
          vignetteDarkness: typeof visual.vignetteDarkness === 'number' ? visual.vignetteDarkness : prev.vignetteDarkness,
          vignetteOffset: typeof visual.vignetteOffset === 'number' ? visual.vignetteOffset : prev.vignetteOffset
        }));
      }

      // Check if any visual property was updated
      const hasVisualUpdate =
        visual.hdrEnvironment !== undefined ||
        visual.envMapIntensity !== undefined ||
        visual.lightingPreset !== undefined;

      if (hasVisualUpdate) {
        console.log('🌅 Updating visual config:', visual);
        setVisualConfig(prev => ({
          ...prev,
          ...visual
        }));
      }
    };

    window.addEventListener('avatar:config:update', handleVisualUpdate as EventListener);
    window.addEventListener('avatar:visual:update', handleVisualUpdate as EventListener);

    return () => {
      window.removeEventListener('avatar:config:update', handleVisualUpdate as EventListener);
      window.removeEventListener('avatar:visual:update', handleVisualUpdate as EventListener);
    };
  }, []);

  useFrame(() => {
    try {
      if (cameraRef.current && motionParallaxEnabled && orientationPermission === 'granted') {
        const { beta, gamma } = deviceOrientation;
        const maxOffset = 0.3;
        const baseBeta = 45;
        const normalizedBeta = THREE.MathUtils.clamp((beta - baseBeta) / 90, -1, 1);
        const normalizedGamma = THREE.MathUtils.clamp(gamma / 90, -1, 1);
        const x = normalizedGamma * maxOffset;
        const y = 1.65 - normalizedBeta * maxOffset;
        const z = 1 - Math.abs(normalizedBeta) * maxOffset * 0.75;
        cameraRef.current.position.lerp(new THREE.Vector3(x, y, z), 0.15);
        cameraRef.current.lookAt(0, 1.65, 0);
      }
    } catch (error) {
      console.error('Frame update error:', error);
    }
  });

  // Get light configs with defaults
  const keyLight = lightingConfig.keyLight || DEFAULT_LIGHTING.keyLight!;
  const fillLight = lightingConfig.fillLight || DEFAULT_LIGHTING.fillLight!;
  const rimLight = lightingConfig.rimLight || DEFAULT_LIGHTING.rimLight!;
  const ambientIntensity = lightingConfig.ambientIntensity ?? DEFAULT_LIGHTING.ambientIntensity!;

  useEffect(() => {
    if (!verboseAvatarDebug) {
      return;
    }

    console.log('[Scene] Mounted', {
      modelUrl,
      modelKey,
      cameraZoom,
      hdrFile,
      useDefaultMaterials,
      useEmbeddedTextures,
      skipTeethAnimation
    });
  }, [cameraZoom, hdrFile, modelKey, modelUrl, skipTeethAnimation, useDefaultMaterials, useEmbeddedTextures, verboseAvatarDebug]);

  return (
    <>
      <OrthographicCamera
        ref={cameraRef}
        makeDefault
        zoom={zoomOverride ?? cameraZoom}
        position={[0, 1.65, 1]}
      />

      {/* Ambient Light */}
      {/* @ts-ignore - R3F JSX element */}
      <ambientLight intensity={ambientIntensity} />

      {/* Key Light - Main light source */}
      {keyLight.enabled && (
        // @ts-ignore - R3F JSX element
        <directionalLight
          intensity={keyLight.intensity}
          color={keyLight.color}
          position={[keyLight.position.x, keyLight.position.y, keyLight.position.z]}
          castShadow
        />
      )}

      {/* Fill Light - Softens shadows */}
      {fillLight.enabled && (
        // @ts-ignore - R3F JSX element
        <directionalLight
          intensity={fillLight.intensity}
          color={fillLight.color}
          position={[fillLight.position.x, fillLight.position.y, fillLight.position.z]}
        />
      )}

      {/* Rim Light - Back edge highlight */}
      {rimLight.enabled && (
        // @ts-ignore - R3F JSX element
        <directionalLight
          intensity={rimLight.intensity}
          color={rimLight.color}
          position={[rimLight.position.x, rimLight.position.y, rimLight.position.z]}
        />
      )}

      <SafeHdrEnvironment files={hdrFile} />

      {/* Save-time portrait capture support: answers head/shoulder
          screen-position queries from the settings modal (see
          PortraitFramingResponder for the wire contract). */}
      <PortraitFramingResponder avatarInstanceId={avatarInstanceId} />

      <Suspense fallback={null}>
        <Avatar
          key={modelKey || modelUrl}
          ref={avatarRef}
          userTalking={userTalking}
          avatar_url={modelUrl}
          speak={speak}
          setSpeak={setSpeak}
          setRecord={setRecord}
          text={text}
          setAudioSource={setAudioSource}
          animateCallSpeak={animateCallSpeak}
          setAnimateCallSpeak={setAnimateCallSpeak}
          setPlaying={setPlaying}
          playing={playing}
          deviceOrientation={deviceOrientation}
          orientationPermission={orientationPermission}
          animateHangupButton={animateHangupButton}
          resetHangupButton={resetHangupButton}
          position={[0, 0, 0]}
          isMobileCall={isMobileCall}
          isWebrtcCall={isWebrtcCall}
          isInVoiceCall={isInVoiceCall}
          isLiveMode={isLiveMode}
          skipTeethAnimation={skipTeethAnimation}
          useDefaultMaterials={useDefaultMaterials}
          useEmbeddedTextures={useEmbeddedTextures}
          initialAppearance={initialAppearance}
          avatarInstanceId={avatarInstanceId}
          onAppearanceApplied={onAppearanceApplied}
        />
      </Suspense>

      <Suspense fallback={null}>
        <Bg
          deviceOrientation={deviceOrientation}
          motionParallaxEnabled={motionParallaxEnabled}
          orientationPermission={orientationPermission}
          avatarInstanceId={avatarInstanceId}
          initialBackground={initialBackground}
        />
      </Suspense>

      {/* Post-processing effects - Disabled: causes crashes with current three.js version.
          The EffectComposer from @react-three/postprocessing triggers
          "Cannot read properties of undefined (reading 'length')" errors
          even when lazy-loaded. Bloom/Vignette effects are not critical.
          TODO: Re-enable when postprocessing library is updated/fixed. */}
    </>
  );
}

// How long to coalesce incoming `avatar:config:update` events before
// committing to a texture load. The mount sequence dispatches the same
// (or near-identical) config from multiple sites within ~150ms —
// CamlinAvatar's `initialConfig` replay, App.tsx's `liveAvatarConfig`
// effect, useHostedLiveControllerEffects' `linkedAvatarConfig` replay,
// plus any `useAvatarRuntimeConfig` fetch resolution. Without this
// debounce each one triggered a fresh `THREE.TextureLoader` fetch and
// the user saw a cascade of background swaps. 200ms is long enough to
// catch the typical burst (the slowest member is the runtime-service
// fetch, which usually settles inside 150ms in production), short
// enough to feel instant.
const BG_EVENT_DEBOUNCE_MS = 200;

// How long to wait after mount with NO `avatar:config:update` event
// before falling back to the legacy default background. Protects the
// ancient consumer that mounts a Scene without ever calling
// `replayAvatarRuntimeConfig` (or any equivalent dispatch). Real
// production consumers always replay within ~250ms of mount, so 1500ms
// is safely past the normal burst.
const BG_FALLBACK_TIMEOUT_MS = 1500;

export function Bg({
  deviceOrientation,
  motionParallaxEnabled,
  orientationPermission,
  avatarInstanceId,
  initialBackground
}: {
  deviceOrientation: DeviceOrientation;
  motionParallaxEnabled?: boolean;
  orientationPermission: string;
  avatarInstanceId?: string;
  initialBackground?: Record<string, string> | null;
}) {
  // Initial state is `null` — no hardcoded `officebackground-hd.webp`
  // flash on first paint. Background truth arrives via one of two paths:
  //   1. The `initialBackground` prop (host-supplied initialAppearance /
  //      initialConfig backgrounds) — applied immediately below, no
  //      window event involved, so it cannot lose a race against either
  //      the legacy-office fallback timer or the Bg mount itself.
  //   2. `avatar:config:update` events (debounced by BG_EVENT_DEBOUNCE_MS
  //      so a burst of replays coalesces into ONE texture load) — these
  //      always win over the prop seed because they represent live
  //      editor/runtime updates.
  // The safety fallback below covers the no-truth-ever case.
  const [bgConfig, setBgConfig] = useState<BackgroundConfig | null>(null);

  const { scene, size } = useThree();
  // Truth tracking for the two paths above. `eventTruth` means an
  // avatar:config:update committed a background — the prop seed must
  // never overwrite it. `backgroundTruth` means ANY truth arrived (prop
  // or event) — the legacy office fallback must not fire.
  const eventTruthRef = useRef(false);
  const backgroundTruthRef = useRef(false);
  const [texture, setTexture] = useState<THREE.Texture | null>(null);
  const fittedTextureFrameRef = useRef({
    offsetX: 0,
    offsetY: 0,
    repeatX: 1,
    repeatY: 1
  });
  // Track the URL of the last-loaded texture so a same-URL event coming
  // through at any point (e.g. live-mode periodic config refresh) doesn't
  // trigger a redundant TextureLoader fetch.
  const lastLoadedUrlRef = useRef<string | null>(null);
  const requestedBackgroundUrl = bgConfig?.backgroundType === 'image'
    ? resolveBackgroundAssetUrl(bgConfig.backgroundImage || DEFAULT_BACKGROUND_IMAGE)
    : null;

  useEffect(() => {
    if (!bgConfig || bgConfig.backgroundType !== 'image' || !requestedBackgroundUrl) {
      setTexture(null);
      lastLoadedUrlRef.current = null;
      return;
    }

    // Same URL already loaded — skip the fetch. Avoids the texture
    // flicker that comes from disposing+reloading an identical bitmap.
    if (lastLoadedUrlRef.current === requestedBackgroundUrl && texture) {
      return;
    }

    if (/\.hdr$/i.test(requestedBackgroundUrl)) {
      console.warn('[Scene] Refusing to load HDR as a 2D background image:', requestedBackgroundUrl);
      setTexture(null);
      lastLoadedUrlRef.current = null;
      return;
    }

    let cancelled = false;
    const loader = new THREE.TextureLoader();
    const loadTexture = (url: string, allowFallback: boolean) => {
      if (/\.hdr$/i.test(url)) {
        console.warn('[Scene] Refusing to load HDR as a 2D background image:', url);
        setTexture(null);
        return;
      }
      loader.load(
        url,
        (loadedTexture) => {
          if (cancelled) {
            loadedTexture.dispose();
            return;
          }
          lastLoadedUrlRef.current = url;
          setTexture((previous) => {
            previous?.dispose();
            return loadedTexture;
          });
        },
        undefined,
        () => {
          if (cancelled) return;
          if (allowFallback) {
            console.warn('[Scene] Background image failed to load; falling back to default:', url);
            loadTexture(resolveBackgroundAssetUrl(DEFAULT_BACKGROUND_IMAGE), false);
          } else {
            console.warn('[Scene] Default background image failed to load:', url);
            setTexture(null);
          }
        }
      );
    };

    loadTexture(requestedBackgroundUrl, requestedBackgroundUrl !== resolveBackgroundAssetUrl(DEFAULT_BACKGROUND_IMAGE));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `texture` is read for the same-URL short-circuit above; including it in deps would re-fire the loader as soon as we set the texture, defeating the dedup
  }, [bgConfig, requestedBackgroundUrl]);

  // Shared apply path for both truth sources (prop seed + config events).
  const applyBackgroundUpdate = useCallback((update: BackgroundUpdate) => {
    if (update.type === 'solid') {
      scene.background = new THREE.Color(update.color);
      setBgConfig({ backgroundType: 'solid', backgroundColor: update.color });
    } else if (update.type === 'gradient') {
      const fallback = update.color || '#667eea';
      scene.background = new THREE.Color(fallback);
      setBgConfig({ backgroundType: 'solid', backgroundColor: fallback });
    } else if (update.type === 'image') {
      // Only commit when the URL actually differs from the currently
      // loaded one. Otherwise we'd re-trigger the texture-load effect
      // (which already has its own same-URL guard, but bailing here
      // keeps React from churning).
      if (lastLoadedUrlRef.current === resolveBackgroundAssetUrl(update.imageUrl)) return;
      setBgConfig({ backgroundType: 'image', backgroundImage: update.imageUrl });
    }
    // type 'none' (e.g. backgroundType 'transparent'): render nothing —
    // callers have already recorded the truth so fallbacks stay quiet.
  }, [scene]);

  // Seed background truth from the `initialBackground` prop (host
  // initialAppearance/initialConfig). Applied immediately — NOT debounced
  // and NOT gated on model readiness — so the correct background is
  // committed long before the legacy office fallback below could fire.
  // Re-runs when the prop arrives late (e.g. the embed's settings fetch
  // resolving), but never overrides truth committed by a config event.
  const lastSeedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialBackground || eventTruthRef.current) return;

    const update = resolveBackgroundUpdate(initialBackground);
    if (!update) return;

    // Hosts often rebuild the prop object every render; dedup by value
    // so identical seeds don't re-apply.
    const seedKey = JSON.stringify(initialBackground);
    if (lastSeedKeyRef.current === seedKey) return;
    lastSeedKeyRef.current = seedKey;

    backgroundTruthRef.current = true;
    applyBackgroundUpdate(update);
  }, [applyBackgroundUpdate, initialBackground]);

  // Coalesce incoming `avatar:config:update` events into ONE applied
  // background within a BG_EVENT_DEBOUNCE_MS window. The previous
  // implementation applied each event synchronously, which during the
  // mount-time replay burst (CamlinAvatar + App.tsx +
  // useHostedLiveControllerEffects) caused 2–3 visible texture loads.
  useEffect(() => {
    const pendingRef: { current: BackgroundUpdate | null } = { current: null };
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    const flushPending = () => {
      const pending = pendingRef.current;
      if (!pending) return;
      pendingRef.current = null;
      applyBackgroundUpdate(pending);
    };

    const scheduleFlush = () => {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        flushPending();
      }, BG_EVENT_DEBOUNCE_MS);
    };

    const handleConfigUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      const payload = customEvent.detail || {};
      if (payload.avatarInstanceId && payload.avatarInstanceId !== avatarInstanceId) {
        return;
      }
      const appearance = payload.config?.appearance || payload.appearance || {};

      const update = resolveBackgroundUpdate(appearance);
      if (!update) {
        // The replay carried no background fields; ignore it for Bg
        // purposes (Avatar.tsx will still apply mesh appearance from
        // the same event). DO NOT record truth here — we want the
        // safety fallback to still fire if no background-bearing
        // truth ever lands.
        return;
      }

      eventTruthRef.current = true;
      backgroundTruthRef.current = true;
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }

      if (update.type === 'none') {
        // Background fields present but nothing renderable (e.g.
        // 'transparent') — truth recorded, nothing to paint.
        return;
      }

      pendingRef.current = update;
      scheduleFlush();
    };

    // Safety fallback for consumers that never replay (legacy or
    // freshly-mounted standalone embeds). After BG_FALLBACK_TIMEOUT_MS
    // with zero background truth (no prop seed, no config event),
    // populate the background with the legacy default so the user
    // doesn't see an empty scene forever.
    fallbackTimer = setTimeout(() => {
      if (!backgroundTruthRef.current) {
        applyBackgroundUpdate({ type: 'image', imageUrl: DEFAULT_BACKGROUND_IMAGE });
      }
      fallbackTimer = null;
    }, BG_FALLBACK_TIMEOUT_MS);

    window.addEventListener('avatar:config:update', handleConfigUpdate);
    return () => {
      window.removeEventListener('avatar:config:update', handleConfigUpdate);
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      if (fallbackTimer !== null) clearTimeout(fallbackTimer);
    };
  }, [applyBackgroundUpdate, avatarInstanceId]);

  useEffect(() => {
    if (!bgConfig || bgConfig.backgroundType !== 'image' || !texture) {
      return;
    }

    if (!isTextureUploadReady(texture)) {
      return;
    }

    const image = texture.image as { width: number; height: number };
    const imageWidth = image.width;
    const imageHeight = image.height;
    const canvasWidth = Math.max(size.width, 1);
    const canvasHeight = Math.max(size.height, 1);
    const imageAspect = imageWidth / Math.max(imageHeight, 1);
    const canvasAspect = canvasWidth / canvasHeight;

    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    if ('colorSpace' in texture) {
      texture.colorSpace = THREE.SRGBColorSpace;
    } else {
      (texture as any).encoding = 3001;
    }

    texture.repeat.set(1, 1);
    texture.offset.set(0, 0);

    if (canvasAspect > imageAspect) {
      const visibleHeight = imageAspect / canvasAspect;
      texture.repeat.y = visibleHeight;
      texture.offset.y = (1 - visibleHeight) * 0.5;
    } else {
      const visibleWidth = canvasAspect / imageAspect;
      texture.repeat.x = visibleWidth;
      texture.offset.x = (1 - visibleWidth) * 0.5;
    }

    fittedTextureFrameRef.current = {
      offsetX: texture.offset.x,
      offsetY: texture.offset.y,
      repeatX: texture.repeat.x,
      repeatY: texture.repeat.y
    };
    texture.needsUpdate = true;
    scene.background = texture;

    return () => {
      if (scene.background === texture) {
        scene.background = null;
      }
    };
  }, [bgConfig, scene, size.height, size.width, texture]);

  useEffect(() => {
    if (motionParallaxEnabled || !texture) {
      return;
    }

    const frame = fittedTextureFrameRef.current;
    texture.offset.set(frame.offsetX, frame.offsetY);
    texture.repeat.set(frame.repeatX, frame.repeatY);
    texture.needsUpdate = true;
  }, [motionParallaxEnabled, texture]);

  useFrame(() => {
    if (!motionParallaxEnabled || orientationPermission !== 'granted' || bgConfig?.backgroundType !== 'image' || !texture) {
      return;
    }

    const { beta, gamma } = deviceOrientation;
    const normalizedBeta = THREE.MathUtils.clamp((beta - 45) / 90, -1, 1);
    const normalizedGamma = THREE.MathUtils.clamp(gamma / 90, -1, 1);
    const frame = fittedTextureFrameRef.current;
    const offsetX = frame.offsetX - normalizedGamma * 0.012;
    const offsetY = frame.offsetY + normalizedBeta * 0.01;

    texture.offset.lerp(new THREE.Vector2(offsetX, offsetY), 0.08);
    texture.repeat.set(frame.repeatX, frame.repeatY);
    texture.needsUpdate = true;
  });

  return null;
}
